import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, mergeMap, Observable, of, switchMap, tap, withLatestFrom, finalize } from 'rxjs';
import {
  createUpdatePropertyListing,
  createUpdatePropertyListingFailure,
  createUpdatePropertyListingSuccess,
  deleteProperty,
  deletePropertyFailure,
  deletePropertySuccess,
  getAllAmenities,
  getAllAmenitiesFailure,
  getAllAmenitiesSuccess,
  getAllPropertyTypes,
  getAllPropertyTypesFailure,
  getAllPropertyTypesSuccess,
  getBookings,
  getBookingsFailure,
  getBookingsSuccess,
  getHomePageListings,
  getHomePageListingsFailure,
  getHomePageListingsSuccess,
  getPropertyDetails,
  getPropertyDetailsFailure,
  getPropertyDetailsSuccess,
  setPropertyStatus,
  setPropertyStatusFailure,
  setPropertyStatusSuccess,
  toggleAvailability,
  toggleAvailabilityFailure,
  toggleAvailabilitySuccess,
  uploadFile,
  uploadFileFailure,
  uploadFileSuccess,
} from './listings-actions';
import { CustomSnackbarComponent } from 'src/app/shared/components/custom-snackbar/custom-snackbar.component';
import { UserService } from 'src/app/services/common-services/user.service';
import { LoaderService } from 'src/app/services/loader-service/loader.service';
import { UserFacadeService } from 'src/app/services/common-services/user-facade.service';
import { Amenity, PropertyType } from 'src/app/shared/models';
import { setSharedData } from '../shared/actions';

@Injectable()
export class ListingsEffects {
  constructor(
    private actions$: Actions,
    private snackBar: MatSnackBar,
    private router: Router,
    private dialog: MatDialog,
    private userService: UserService,
    private loaderService: LoaderService,
    private userFacade: UserFacadeService,
  ) {}

  getHomePageListingsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getHomePageListings),
      tap(() => {
        this.loaderService.setLoading(true);
      }),
      withLatestFrom(this.userFacade.listProperties$),
      switchMap(([action, listProperties]) =>
        this.userService.getHomePageListings(action.data).pipe(
          mergeMap((res) => {
            return [
              getHomePageListingsSuccess({
                data: action.isLazyLoad
                  ? {
                      ...res,
                      result: [...listProperties, ...(Array.isArray(res.result) ? res.result : [])],
                    }
                  : res,
              }),
              setSharedData({ data: res }),
            ];
          }),
          catchError((error) => {
            return of(getHomePageListingsFailure({ error }));
          }),
          finalize(() => {
            this.loaderService.setLoading(false);
          }),
        ),
      ),
    );
  });

  getAllAmenitiesEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllAmenities),
      switchMap(() =>
        this.userService.getAllAmenities().pipe(
          mergeMap((res) => {
            return [
              getAllAmenitiesSuccess({
                amenities: res.result as Amenity[],
              }),
            ];
          }),
          catchError((error) => {
            return of(getAllAmenitiesFailure({ error }));
          }),
        ),
      ),
    );
  });

  getAllPropertyTypesEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getAllPropertyTypes),
      switchMap(() =>
        this.userService.getAllPropertyTypes().pipe(
          mergeMap((res) => {
            return [
              getAllPropertyTypesSuccess({
                propertyTypes: res.result as PropertyType[],
              }),
            ];
          }),
          catchError((error) => {
            return of(getAllPropertyTypesFailure({ error }));
          }),
        ),
      ),
    );
  });

  uploadFileEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(uploadFile),
      switchMap((action) =>
        this.userService.uploadFile(action.file, action.imageType).pipe(
          mergeMap((res) => {
            return [uploadFileSuccess({ data: res, imageType: action.imageType })];
          }),
          catchError((error) => {
            return of(uploadFileFailure({ error, imageType: action.imageType }));
          }),
        ),
      ),
    );
  });

  createUpdatePropertyListingEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(createUpdatePropertyListing),
      tap((action) => {
        if (action.isDraft) {
          this.snackBar.openFromComponent(CustomSnackbarComponent, {
            data: {
              title: `Auto saving...`,
              type: 'info',
            },
            panelClass: 'info',
            duration: 3000,
            horizontalPosition: 'start',
          });
        } else {
          this.snackBar.openFromComponent(CustomSnackbarComponent, {
            data: {
              title: `Submitting your listing...`,
              type: 'info',
            },
            panelClass: 'info',
            duration: 3000,
            horizontalPosition: 'start',
          });
        }
      }),
      switchMap((action) =>
        this.userService.createUpdatePropertyListing(action.data, action.isDraft).pipe(
          tap((res) => {
            if (action.isDraft && !action.data?.id && res?.result?.id) {
              this.router.navigate(['dashboard', 'edit-listings', res.result.id]);
            }
            if (!action.isDraft && res.successful) {
              this.snackBar.openFromComponent(CustomSnackbarComponent, {
                data: {
                  title: `Your listing has been submitted successfully!`,
                  type: 'success',
                },
                panelClass: 'success',
                duration: 3000,
                horizontalPosition: 'start',
              });

              this.router.navigate(['dashboard', 'my-listings', 'Pending']);
            }
          }),
          mergeMap((res) => {
            return [createUpdatePropertyListingSuccess({ data: res })];
          }),
          catchError((error) => {
            return of(createUpdatePropertyListingFailure({ error }));
          }),
        ),
      ),
    );
  });

  getPropertyDetailsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getPropertyDetails),
      switchMap((action) =>
        this.userService.getPropertyById(action.propertyId).pipe(
          mergeMap((res) => {
            return [getPropertyDetailsSuccess({ data: res })];
          }),
          catchError((error) => {
            return of(getPropertyDetailsFailure({ error }));
          }),
        ),
      ),
    );
  });

  setPropertyStatusEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(setPropertyStatus),
      switchMap((action) =>
        this.userService.setPropertyStatus(action.data).pipe(
          tap(() => {
            const successMessage =
              action.data.propertyStatus === 'Approved'
                ? 'Listing has been approved successfully!'
                : action.data.propertyStatus === 'Rejected'
                  ? 'Listing has been rejected successfully!'
                  : 'Listing status has been updated successfully!';
            this.snackBar.openFromComponent(CustomSnackbarComponent, {
              data: {
                title: successMessage,
                type: 'success',
              },
              panelClass: 'success',
              duration: 3000,
              horizontalPosition: 'start',
            });
          }),
          mergeMap((res) => {
            return [
              setPropertyStatusSuccess({ data: res }),
              getHomePageListings({ data: action.searchData || {}, isLazyLoad: false }),
            ];
          }),
          catchError((error) => {
            return of(setPropertyStatusFailure({ error }));
          }),
        ),
      ),
    );
  });

  deletePropertyEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(deleteProperty),
      switchMap((action) =>
        this.userService.deleteProperty(action.propertyId).pipe(
          tap(() => {
            this.snackBar.openFromComponent(CustomSnackbarComponent, {
              data: {
                title: `Listing has been deleted successfully!`,
                type: 'success',
              },
              panelClass: 'success',
              duration: 3000,
              horizontalPosition: 'start',
            });
          }),
          mergeMap((res) => {
            return [
              deletePropertySuccess({ data: res }),
              getHomePageListings({ data: action.searchData || {}, isLazyLoad: false }),
            ];
          }),
          catchError((error) => {
            return of(deletePropertyFailure({ error }));
          }),
        ),
      ),
    );
  });

  getBookingsEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(getBookings),
      switchMap((action) =>
        this.userService.getBookings(action.data, action.url).pipe(
          mergeMap((res) => {
            return [getBookingsSuccess({ data: res }), setSharedData({ data: res })];
          }),
          catchError((error) => {
            return of(getBookingsFailure({ error }));
          }),
        ),
      ),
    );
  });

  toggleAvailabilityEffect$: Observable<Action> = createEffect(() => {
    return this.actions$.pipe(
      ofType(toggleAvailability),
      switchMap((action) =>
        this.userService.toggleAvailability(action.data).pipe(
          tap(() => {
            const successMessage =
              action.data.isAvailable === false
                ? 'Listing has been marked as unavailable!'
                : 'Listing has been marked as available!';
            this.snackBar.openFromComponent(CustomSnackbarComponent, {
              data: {
                title: successMessage,
                type: 'success',
              },
              panelClass: 'success',
              duration: 3000,
              horizontalPosition: 'start',
            });
          }),
          mergeMap((res) => {
            return [
              toggleAvailabilitySuccess({ data: res }),
              getHomePageListings({ data: action.searchData || {}, isLazyLoad: false }),
            ];
          }),
          catchError((error) => {
            return of(toggleAvailabilityFailure({ error }));
          }),
        ),
      ),
    );
  });
}
